/**
 * The campus.
 *
 * ---------------------------------------------------------------------
 * THE SCREEN IS THE POINT
 *
 * 0210 built one vocabulary for places so that four unbuilt ideas — a
 * lost-property book, poolside ordering, cart tracking, an incident log
 * — do not each invent their own list of court names. None of those four
 * exists yet.
 *
 * Which means that on the day this ships, the ONLY consumer of the venue
 * registry is a club naming its own campus. A registry nothing can write
 * to is the failure this codebase has now found four separate times: the
 * pool, the agronomy log, the member's own account, and a sign-in that
 * returned an empty array. Every one of them was finished, green and
 * unreachable. Shipping a fifth knowingly would be a choice rather than
 * an oversight.
 *
 * ---------------------------------------------------------------------
 * ADOPT IS THE FIRST THING ON THE PAGE
 *
 * A club that must hand-type sixty rows types none, the registry stays
 * empty, and in March somebody adds a court list to the ordering feature
 * because the venue table had nothing in it.
 *
 * So the empty state is not an apology, it is a button. `fn_venue_adopt`
 * reads the holes, courts, lanes, docks, bays and counters the club
 * already has and names them. It is idempotent, and this reports what it
 * made per zone, because "nothing happened" and "there was nothing to
 * do" look identical otherwise — and on every press after the first, the
 * second is the correct answer.
 *
 * ---------------------------------------------------------------------
 * THE DATABASE OWNS THE TREE
 *
 * `v_venue` computes both the path and the depth, and the rows arrive
 * ordered by path. Indenting a pre-ordered list on a number the database
 * gave us is the whole of the layout — and unlike walking `parent_id`
 * here, it cannot come to a different conclusion about who is inside
 * whom than `fn_venue_descendants` does.
 */

import { useMemo, useState } from 'react';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { Card, Empty, ErrorBanner, Loading, Status } from '../components/ui.tsx';

interface Venue {
  id: string;
  code: string;
  name: string;
  root_name: string;
  kind: string;
  parent_id: string | null;
  path: string;
  depth: number;
  department_id: string | null;
  department: string | null;
  note: string | null;
  resource_kind: string | null;
  has_geofence: boolean;
  latitude: string | null;
  longitude: string | null;
  radius_m: string | null;
  is_active: boolean;
}

interface Department {
  id: string;
  name: string;
}

interface Payload {
  venues: Venue[];
  departments: Department[];
  kinds: string[];
}

interface Adopted {
  zone: string;
  created: number;
}

/**
 * What each `kind` is, in the words a club uses.
 *
 * The column is a closed CHECK on purpose — a free-text kind is four
 * vocabularies again with extra steps — but `station`, `water` and
 * `ground` are database words. A manager choosing from a menu should
 * read what they mean, not what they are stored as. This is the same
 * mistake `pool_session.purpose` made on a member's own screen, where
 * somebody read "open_swim".
 */
const KIND_SAYS: Record<string, string> = {
  zone: 'Zone — a part of the campus, with things inside it',
  hole: 'Hole',
  tee: 'Tee',
  court: 'Court',
  station: 'Station or bay',
  water: 'Water — a pool, a lane, a beach',
  dock: 'Dock or slip',
  room: 'Room',
  counter: 'Counter — somewhere a member can be charged',
  storage: 'Storage',
  parking: 'Parking',
  ground: 'Ground — turf, a green, a practice area',
  other: 'Somewhere else',
};

/** The short label, for a row rather than a menu. */
function kindLabel(kind: string): string {
  return (KIND_SAYS[kind] ?? kind).split(' — ')[0]!;
}

/**
 * Telling two places with the same name apart.
 *
 * A club with two pools has two Lane 1s. `fn_venue_adopt` files each one
 * under its own pool, so naming the parent settles it — "Lane 1 · in
 * Main Pool".
 *
 * When the parent does NOT settle it — two courts both called Court 1,
 * both directly under Racquet Sports, differing only in surface — the
 * code does, because the code is unique by construction. It is a worse
 * label and it is the correct fallback: a row a person cannot tell from
 * the row above it is a row they will act on by accident.
 *
 * Nothing at all for the ninety rows whose names are already unique.
 */
function disambiguate(
  v: Venue,
  all: Venue[],
  ambiguous: Set<string>,
  parentOf: Map<string, string>,
) {
  if (!ambiguous.has(v.name)) return null;

  const twins = all.filter((o) => o.name === v.name);
  const parentSettlesIt =
    v.parent_id !== null &&
    twins.every((o) => o.parent_id !== null) &&
    new Set(twins.map((o) => o.parent_id)).size === twins.length;

  return (
    <span className="muted">
      {parentSettlesIt
        ? ` · in ${parentOf.get(v.parent_id!) ?? '—'}`
        : ` · ${v.code}`}
    </span>
  );
}

/**
 * What a link means, for the one case where the kind does not say it.
 *
 * The first draft printed this beside every kind, and the column read
 * "Hole · a hole on the course", "Court · a court", "Water · water" —
 * the same fact twice, ninety times down the page. What a reader
 * actually needs from the link is one bit: is this venue the software's
 * own record of that court, or a name somebody typed? Renaming the first
 * is safe; the second has nothing behind it.
 *
 * So it is a single muted word, and only `outlet` gets a gloss, because
 * a `counter` that is a till and a `counter` that is a bag drop are
 * genuinely different things.
 */
const LINK_SAYS: Record<string, string> = {
  outlet: 'a till',
};

export function CampusPage() {
  const places = useApi<Payload>('/v1/venues?include_closed=true');
  const [adopted, setAdopted] = useState<Adopted[] | null>(null);
  const [editing, setEditing] = useState<Venue | null>(null);
  const [adding, setAdding] = useState(false);

  const adopt = useMutation(async () => {
    const r = await api<{ adopted: Adopted[]; created: number }>(
      '/v1/venues/adopt', { method: 'POST' },
    );
    setAdopted(r.adopted);
    places.reload();
    return r;
  });

  const venues = places.data?.venues ?? [];
  const fenced = useMemo(() => venues.filter((v) => v.has_geofence).length, [venues]);
  const closed = useMemo(() => venues.filter((v) => !v.is_active).length, [venues]);

  const parentOf = useMemo(
    () => new Map(venues.map((v) => [v.id, v.name])), [venues],
  );

  /** Names that appear more than once, and so cannot stand alone. */
  const ambiguous = useMemo(() => {
    const seen = new Map<string, number>();
    for (const v of venues) seen.set(v.name, (seen.get(v.name) ?? 0) + 1);
    return new Set([...seen].filter(([, n]) => n > 1).map(([name]) => name));
  }, [venues]);

  if (places.loading && !places.data) return <Loading />;

  return (
    <>
      <h1>The Campus</h1>
      <ErrorBanner error={places.error} />
      <ErrorBanner error={adopt.error} />

      {venues.length === 0 ? (
        /* The empty state IS the button. See the header. */
        <Card title="Name the Campus">
          <Empty>
            Nothing here has a name yet. The club already has holes, courts, lanes, docks and
            tills in the system — naming them takes one press, and everything that has to say
            where something is will use these names.
          </Empty>
          <div className="actions">
            <button disabled={adopt.busy} onClick={() => adopt.run()}>
              {adopt.busy ? 'Naming…' : 'Name them from what the club has'}
            </button>
            <span className="muted small">
              Nothing is replaced or renamed. Running it again later picks up only what is new.
            </span>
          </div>
        </Card>
      ) : (
        <Card title="The Places at This Club">
          <div className="tiles">
            <div className="tile">
              <div className="tile__label">Named</div>
              <div className="tile__value">{venues.length}</div>
              <div className="tile__note">{closed > 0 ? `${closed} closed` : 'all open'}</div>
            </div>
            <div className="tile">
              <div className="tile__label">With a boundary</div>
              <div className="tile__value">{fenced}</div>
              <div className="tile__note">
                {fenced === 0 ? 'none drawn yet' : 'a phone can tell it is there'}
              </div>
            </div>
          </div>
          <div className="actions">
            <button className="secondary" disabled={adopt.busy} onClick={() => adopt.run()}>
              {adopt.busy ? 'Checking…' : 'Pick up anything new'}
            </button>
            <button onClick={() => { setAdding(true); setEditing(null); }}>
              Add a place
            </button>
          </div>
        </Card>
      )}

      {/* What the button did, per zone. A function that silently creates
          sixty rows is one nobody dares press a second time. */}
      {adopted && (
        <Card title="What That Named">
          {adopted.every((a) => a.created === 0) ? (
            <p className="muted">
              Everything the club has was already named. Nothing changed.
            </p>
          ) : (
            <ul className="agenda">
              {adopted.filter((a) => a.created > 0).map((a) => (
                <li key={a.zone} className="agenda__item">
                  <span className="agenda__time">{a.created}</span>
                  <span className="agenda__what">{a.zone}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {(adding || editing) && (
        <VenueForm
          key={editing?.id ?? 'new'}
          venue={editing}
          departments={places.data?.departments ?? []}
          kinds={places.data?.kinds ?? []}
          venues={venues}
          onDone={() => { setAdding(false); setEditing(null); places.reload(); }}
          onCancel={() => { setAdding(false); setEditing(null); }}
        />
      )}

      {venues.length > 0 && (
        <Card title="Everywhere">
          <div className="table">
            <table className="data data--cards">
              <thead>
                <tr>
                  <th>Place</th>
                  <th>What</th>
                  <th>Department</th>
                  <th>Boundary</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {venues.map((v) => (
                  <tr key={v.id}>
                    <td data-label="Place" className="cell--title">
                      {/* The indent is the tree, on the depth the
                          database computed. Walking parent_id here would
                          be a second opinion about the same shape. */}
                      <span style={{ paddingLeft: `${Math.min(v.depth, 6) * 14}px` }}>
                        {v.name}
                        {/* WHERE, WHEN THE NAME IS NOT ENOUGH.
                            A club with two pools has two Lane 1s, and
                            two racquet surfaces have two Court 1s. The
                            indent shows there is a parent; it does not
                            show WHICH, and a list with "Lane 1" twice in
                            it is a list nobody can act on. Shown only
                            for the names that actually repeat, so the
                            other ninety rows stay quiet. */}
                        {disambiguate(v, venues, ambiguous, parentOf)}
                      </span>
                      {!v.is_active && <> <Status tone="neutral">closed</Status></>}
                      {v.note ? <div className="muted small">{v.note}</div> : null}
                    </td>
                    <td data-label="What" className="muted small">
                      {kindLabel(v.kind)}
                      {/* A venue that IS a resource says so in one word.
                          The difference between a place the club typed
                          and a place the software already knew about is
                          the difference between renaming it and breaking
                          a link — and it is one bit, not a sentence. */}
                      {v.resource_kind
                        ? ` · ${LINK_SAYS[v.resource_kind] ?? 'linked'}`
                        : ''}
                    </td>
                    <td data-label="Department" className="muted small">
                      {v.department ?? '—'}
                    </td>
                    <td data-label="Boundary" className="muted small">
                      {v.has_geofence ? `${Number(v.radius_m)} m` : '—'}
                    </td>
                    <td className="cell--action">
                      <button className="secondary small"
                              onClick={() => { setEditing(v); setAdding(false); }}>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </>
  );
}

/**
 * Adding and editing, and the one asymmetry between them.
 *
 * A new place may be linked to nothing at all — the club is naming a
 * coat check, a car park, a terrace. An EXISTING place's link is not
 * editable here, and that is VENUE-3 working rather than a gap: a club
 * that wants "Court 3" to mean a different court has made a different
 * place, not renamed this one. Moving the link under an established
 * venue would silently move everything ever routed to it.
 *
 * The boundary is three fields together or none, because VENUE-4 refuses
 * two of the three, and a form that can save one at a time is a form
 * that can only fail.
 */
function VenueForm({
  venue, departments, kinds, venues, onDone, onCancel,
}: {
  venue: Venue | null;
  departments: Department[];
  kinds: string[];
  venues: Venue[];
  onDone: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(venue?.name ?? '');
  const [code, setCode] = useState('');
  const [kind, setKind] = useState(venue?.kind ?? 'zone');
  const [parentId, setParentId] = useState(venue?.parent_id ?? '');
  const [departmentId, setDepartmentId] = useState(venue?.department_id ?? '');
  const [note, setNote] = useState(venue?.note ?? '');
  const [isActive, setIsActive] = useState(venue?.is_active ?? true);

  const [lat, setLat] = useState(venue?.latitude ?? '');
  const [lon, setLon] = useState(venue?.longitude ?? '');
  const [radius, setRadius] = useState(venue?.radius_m ?? '');

  const save = useMutation(async () => {
    if (venue) {
      await api(`/v1/venues/${venue.id}`, {
        method: 'PATCH',
        body: {
          name, kind,
          parent_id: parentId === '' ? null : parentId,
          department_id: departmentId === '' ? null : departmentId,
          note: note.trim() === '' ? null : note.trim(),
          is_active: isActive,
        },
      });
    } else {
      await api('/v1/venues', {
        method: 'POST',
        body: {
          code, name, kind,
          parent_id: parentId === '' ? null : parentId,
          department_id: departmentId === '' ? null : departmentId,
          note: note.trim() === '' ? null : note.trim(),
        },
      });
    }
    onDone();
  });

  // All three or none. The API refuses the in-between with a sentence;
  // this refuses to send it in the first place.
  const drawn = lat !== '' && lon !== '' && radius !== '';
  const cleared = lat === '' && lon === '' && radius === '';

  const fence = useMutation(async () => {
    if (!venue) return;
    await api(`/v1/venues/${venue.id}/geofence`, {
      method: 'PUT',
      body: drawn
        ? { latitude: Number(lat), longitude: Number(lon), radius_m: Number(radius) }
        : { latitude: null, longitude: null, radius_m: null },
    });
    onDone();
  });

  return (
    <Card title={venue ? `Editing ${venue.name}` : 'A New Place'}>
      <ErrorBanner error={save.error} />
      <ErrorBanner error={fence.error} />

      <div className="row row--wrap" style={{ gap: 12, alignItems: 'flex-end' }}>
        <label>
          <span className="muted small">Name</span>
          <input value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
        </label>

        {!venue && (
          <label>
            <span className="muted small">Short code</span>
            <input value={code} maxLength={24} className="mono"
                   onChange={(e) =>
                     setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''))} />
          </label>
        )}

        <label>
          <span className="muted small">What it is</span>
          <select value={kind} onChange={(e) => setKind(e.target.value)}>
            {kinds.map((k) => <option key={k} value={k}>{KIND_SAYS[k] ?? k}</option>)}
          </select>
        </label>

        <label>
          <span className="muted small">Inside</span>
          <select value={parentId} onChange={(e) => setParentId(e.target.value)}>
            <option value="">Nowhere — a place of its own</option>
            {venues
              .filter((v) => v.id !== venue?.id)
              .map((v) => <option key={v.id} value={v.id}>{v.path}</option>)}
          </select>
        </label>

        <label>
          <span className="muted small">Department</span>
          <select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
            <option value="">None</option>
            {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </label>

        <label style={{ flex: 1, minWidth: 220 }}>
          <span className="muted small">Note</span>
          <input value={note} maxLength={1000} style={{ width: '100%' }}
                 onChange={(e) => setNote(e.target.value)} />
        </label>
      </div>

      {!venue && (
        <p className="muted small">
          The short code is capitals, digits and underscores, and is how anything referring to
          this place finds it without looking the name up.
        </p>
      )}

      <div className="actions">
        {venue && (
          <label className="row" style={{ gap: 6 }}>
            <input type="checkbox" checked={isActive} style={{ width: 'auto' }}
                   onChange={(e) => setIsActive(e.target.checked)} />
            <span className="muted small">Open</span>
          </label>
        )}
        <button disabled={save.busy || name.trim() === '' || (!venue && code === '')}
                onClick={() => save.run()}>
          {save.busy ? 'Saving…' : venue ? 'Save' : 'Add it'}
        </button>
        <button className="secondary" onClick={onCancel}>Cancel</button>
      </div>

      {venue && (
        <>
          <h3 style={{ marginTop: 22 }}>The Boundary</h3>
          <p className="muted small">
            A center and a distance, so a phone standing inside it can tell where it is. Worth
            drawing only somewhere a member would order something or report something from.
            All three together, or clear all three.
          </p>
          <div className="row row--wrap" style={{ gap: 12, alignItems: 'flex-end' }}>
            <label>
              <span className="muted small">Latitude</span>
              <input value={lat} inputMode="decimal" className="mono"
                     onChange={(e) => setLat(e.target.value)} />
            </label>
            <label>
              <span className="muted small">Longitude</span>
              <input value={lon} inputMode="decimal" className="mono"
                     onChange={(e) => setLon(e.target.value)} />
            </label>
            <label>
              <span className="muted small">Distance in meters</span>
              <input value={radius} inputMode="decimal" className="mono"
                     onChange={(e) => setRadius(e.target.value)} />
            </label>
          </div>
          <div className="actions">
            {/* Nothing to remove from a venue that never had one. The
                first draft offered "Remove the boundary" on all
                ninety-seven places, none of which had a boundary — a
                button whose only possible effect is nothing, which reads
                as the screen not knowing what it is looking at. */}
            {!(cleared && !venue.has_geofence) && (
              <button className="secondary" disabled={fence.busy || (!drawn && !cleared)}
                      onClick={() => fence.run()}>
                {fence.busy ? 'Saving…' : cleared ? 'Remove the boundary' : 'Save the boundary'}
              </button>
            )}
            {!drawn && !cleared && (
              <span className="muted small">
                Two out of three is a boundary with a gap in it — fill in the third, or empty
                all three.
              </span>
            )}
            {cleared && !venue.has_geofence && (
              <span className="muted small">
                No boundary here. Fill in all three to draw one.
              </span>
            )}
          </div>
        </>
      )}
    </Card>
  );
}
